import {
  Stack,
  Box,
  Heading,
  Container,
  Text,
  SimpleGrid,
  Icon,
  useColorModeValue,
  Image,
  Flex
} from '@chakra-ui/react'
import { FaCarSide, FaChartLine, FaReceipt } from 'react-icons/fa'
import { motion } from 'framer-motion'
import HeroImage from '../assets/hero_dashboard.png'
import VehiclesImage from '../assets/vehicles_list.png'
import ExpensesImage from '../assets/expenses_list.png'

const MotionBox = motion(Box)
const MotionStack = motion(Stack)

export default function Home() {
  const bgGradient = useColorModeValue(
    'radial(blue.50 1px, transparent 1px)',
    'radial(blue.900 1px, transparent 1px)'
  )

  return (
    <Box minH="100vh" w="100vw" overflowX="hidden">
      {/* Hero Section */}
      <Box
        position="relative"
        bg={useColorModeValue('gray.50', 'gray.900')}
        pt={{ base: 12, md: 20 }}
        pb={{ base: 20, md: 32 }}
        overflow="hidden"
      >
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bgImage={bgGradient}
          bgSize="20px 20px"
          opacity="0.5"
          pointerEvents="none"
        />

        <Container maxW={'container.xl'} position="relative" zIndex={1}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 10, md: 20 }} alignItems="center">

            {/* Left Content */}
            <MotionStack
              spacing={{ base: 8, md: 10 }}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              textAlign={{ base: 'center', md: 'left' }}
              alignItems={{ base: 'center', md: 'flex-start' }}
            >
              <Stack spacing={4}>
                <Text
                  textTransform={'uppercase'}
                  color={'blue.400'}
                  fontWeight={600}
                  fontSize={'sm'}
                  bg={useColorModeValue('blue.50', 'blue.900')}
                  p={2}
                  alignSelf={{ base: 'center', md: 'flex-start' }}
                  rounded={'full'}
                  px={4}
                >
                  Now Available on iOS
                </Text>
                <Heading
                  as="h1"
                  fontWeight={800}
                  fontSize={{ base: '4xl', md: '5xl', lg: '6xl' }}
                  lineHeight={'110%'}
                >
                  Track vehicle expenses <br />
                  <Text as={'span'} bgGradient="linear(to-r, blue.400, teal.400)" bgClip="text">
                    effortlessly
                  </Text>
                </Heading>
                <Text color={'gray.500'} maxW={'3xl'} fontSize={{ base: 'lg', md: 'xl' }}>
                  VehicleLogi automates your vehicle expense tracking. From mileage logs to maintenance records,
                  simplify your tax deductions and get clear insights into your vehicle costs.
                </Text>
              </Stack>

              <Stack
                spacing={{ base: 4, sm: 6 }}
                direction={{ base: 'column', sm: 'row' }}
              >
                <Box
                  as={'a'}
                  href="https://apps.apple.com/us/app/vehiclelogi/id6747911854"
                  target="_blank"
                  rel="noopener noreferrer"
                  transition="all 0.3s"
                  _hover={{ transform: 'scale(1.05)' }}
                >
                  <Image
                    src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                    alt="Download VehicleLogi on the App Store"
                    h={'50px'}
                  />
                </Box>
              </Stack>
            </MotionStack>

            {/* Right Image */}
            <MotionBox
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              position="relative"
              display="flex"
              justifyContent="center"
            >
              <Box
                rounded="3xl"
                overflow="hidden"
                boxShadow="2xl"
                aspectRatio={9 / 17.5}
              >
                <Image
                  src={HeroImage}
                  alt="VehicleLogi Dashboard"
                  w="full"
                  h="full"
                  objectFit="cover"
                  objectPosition="bottom"
                />
              </Box>
              {/* Decorative elements behind the phone */}
              <Box
                position="absolute"
                top="-10%"
                right="-10%"
                bottom="-10%"
                left="-10%"
                bgGradient="radial(blue.400 1px, transparent 1px)"
                bgSize="20px 20px"
                opacity="0.2"
                zIndex="-1"
              />
            </MotionBox>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Features Showcase Section */}
      <Box py={{ base: 20, md: 32 }} bg={useColorModeValue('white', 'gray.800')}>
        <Container maxW={'container.xl'}>
          <Stack spacing={4} as={Container} maxW={'3xl'} textAlign={'center'} mb={24}>
            <Heading as="h2" fontSize={{ base: '3xl', md: '4xl' }} fontWeight={700}>
              Why Choose VehicleLogi?
            </Heading>
            <Text color={'gray.500'} fontSize={{ base: 'lg', md: 'xl' }}>
              Everything you need to manage your vehicle expenses, optimized for freelancers and small business owners.
            </Text>
          </Stack>

          {/* Feature 1: Vehicles List */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 10, md: 24 }} alignItems="center" mb={24}>
            <Box order={{ base: 2, md: 1 }}>
              <Heading as="h3" fontSize={{ base: '2xl', md: '3xl' }} mb={4} fontWeight={700}>
                Manage Multiple Vehicles
              </Heading>
              <Text color={'gray.500'} fontSize={'lg'} mb={6}>
                Keep track of your entire fleet, whether it's one car or ten.
                Easily switch between vehicles to see their specific maintenance history,
                current mileage, and spending summaries at a glance.
              </Text>
              <Stack direction="row" spacing={4} align="center">
                <Flex
                  w={10}
                  h={10}
                  align={'center'}
                  justify={'center'}
                  color={'white'}
                  rounded={'full'}
                  bg={'blue.400'}
                >
                  <Icon as={FaCarSide} w={5} h={5} />
                </Flex>
                <Text fontWeight={600}>Unlimited Vehicle Profiles</Text>
              </Stack>
            </Box>
            <Box order={{ base: 1, md: 2 }} position="relative" display="flex" justifyContent="center">
              <Box
                rounded="3xl"
                overflow="hidden"
                boxShadow="2xl"
                maxW={{ base: '280px', md: '320px' }}
                w="full"
                aspectRatio={9 / 17.5}
              >
                <Image
                  src={VehiclesImage}
                  alt="Vehicles List"
                  w="full"
                  h="full"
                  objectFit="cover"
                  objectPosition="bottom"
                />
              </Box>
            </Box>
          </SimpleGrid>

          {/* Feature 2: Expenses List */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 10, md: 24 }} alignItems="center">
            <Box order={{ base: 1, md: 1 }} position="relative" display="flex" justifyContent="center">
              <Box
                rounded="3xl"
                overflow="hidden"
                boxShadow="2xl"
                maxW={{ base: '280px', md: '320px' }}
                w="full"
                aspectRatio={9 / 17.5}
              >
                <Image
                  src={ExpensesImage}
                  alt="Expenses List"
                  w="full"
                  h="full"
                  objectFit="cover"
                  objectPosition="bottom"
                />
              </Box>
            </Box>
            <Box order={{ base: 2, md: 2 }}>
              <Heading as="h3" fontSize={{ base: '2xl', md: '3xl' }} mb={4} fontWeight={700}>
                Detailed Expense Tracking
              </Heading>
              <Text color={'gray.500'} fontSize={'lg'} mb={6}>
                Log every fill-up, service, and repair in seconds.
                Categorize expenses to understand exactly where your money goes.
                Never lose track of a receipt again.
              </Text>
              <Stack spacing={4}>
                <Stack direction="row" spacing={4} align="center">
                  <Flex
                    w={10}
                    h={10}
                    align={'center'}
                    justify={'center'}
                    color={'white'}
                    rounded={'full'}
                    bg={'teal.400'}
                  >
                    <Icon as={FaReceipt} w={5} h={5} />
                  </Flex>
                  <Text fontWeight={600}>Smart Categories & Search</Text>
                </Stack>
                <Stack direction="row" spacing={4} align="center">
                  <Flex
                    w={10}
                    h={10}
                    align={'center'}
                    justify={'center'}
                    color={'white'}
                    rounded={'full'}
                    bg={'purple.400'}
                  >
                    <Icon as={FaChartLine} w={5} h={5} />
                  </Flex>
                  <Text fontWeight={600}>Monthly Spending Insights</Text>
                </Stack>
              </Stack>
            </Box>
          </SimpleGrid>

        </Container>
      </Box>

      {/* Footer / Bottom CTA */}
      <Box py={20} textAlign="center" bg={useColorModeValue('gray.50', 'gray.900')}>
        <Container maxW={'container.md'}>
          <Heading as="h3" mb={6} fontSize="3xl">Ready to get started?</Heading>
          <Text mb={8} color="gray.500" fontSize="lg">
            Download VehicleLogi today and take control of your automotive finances.
          </Text>
          <Box
            as={'a'}
            href="https://apps.apple.com/us/app/vehiclelogi/id6747911854"
            target="_blank"
            rel="noopener noreferrer"
            display="inline-block"
            transition="all 0.3s"
            _hover={{ transform: 'scale(1.05)' }}
          >
            <Image
              src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
              alt="Download VehicleLogi on the App Store"
              h={'50px'}
            />
          </Box>
        </Container>
      </Box>
    </Box>
  )
}